import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import ErrorBoundary from "@/components/ErrorBoundary";

const Web3Provider = lazy(() => import("@/components/Web3Provider"));
const Index = lazy(() => import("./pages/Index"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy-loaded marketing pages (code splitting for performance)
const SharedPortfolioPage = lazy(() => import("./pages/SharedPortfolio"));
const InstallPage = lazy(() => import("./pages/InstallPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const GuidesPage = lazy(() => import("./pages/GuidesPage"));
const GlossaryPage = lazy(() => import("./pages/GlossaryPage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const ChangelogPage = lazy(() => import("./pages/ChangelogPage"));
const ChainsPage = lazy(() => import("./pages/ChainsPage"));
const UseCasesPage = lazy(() => import("./pages/UseCasesPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const ToolsPage = lazy(() => import("./pages/ToolsPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage"));
const SecurityPage = lazy(() => import("./pages/SecurityPage"));
const TestimonialsPage = lazy(() => import("./pages/TestimonialsPage"));
const AlternativesPage = lazy(() => import("./pages/AlternativesPage"));
const EcosystemPage = lazy(() => import("./pages/EcosystemPage"));
const LanguageLandingPage = lazy(() => import("./pages/LanguageLandingPage"));
const TokenLandingPage = lazy(() => import("./pages/TokenLandingPage"));
const DeFiProtocolPage = lazy(() => import("./pages/DeFiProtocolPage"));
const LearnPage = lazy(() => import("./pages/LearnPage"));
const SEOLandingPage = lazy(() => import("./pages/SEOLandingPage"));
const ResearchPage = lazy(() => import("./pages/ResearchPage"));
const RoadmapPage = lazy(() => import("./pages/RoadmapPage"));
const AudiencePage = lazy(() => import("./pages/AudiencePage"));
const WhyChoosePage = lazy(() => import("./pages/WhyChoosePage"));
const CompareRivalPage = lazy(() => import("./pages/CompareRivalPage"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const StatusPage = lazy(() => import("./pages/StatusPage"));
const CommunityPage = lazy(() => import("./pages/CommunityPage"));
const ApiDocsPage = lazy(() => import("./pages/ApiDocsPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const WhatsNewPage = lazy(() => import("./pages/WhatsNewPage"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  return user ? <>{children}</> : <Navigate to="/auth" replace />;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/" replace /> : <>{children}</>;
};

const Web3Wrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>
    <Web3Provider>{children}</Web3Provider>
  </Suspense>
);

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <LanguageProvider>
          <ThemeProvider>
            <CurrencyProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <Suspense fallback={<PageLoader />}>
                    <main>
                    <Routes>
                      <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
                      <Route path="/shared/:token" element={<SharedPortfolioPage />} />
                      <Route path="/install" element={<InstallPage />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/features" element={<FeaturesPage />} />
                      <Route path="/guides" element={<GuidesPage />} />
                      <Route path="/guides/:slug" element={<GuidesPage />} />
                      <Route path="/glossary" element={<GlossaryPage />} />
                      <Route path="/compare" element={<ComparePage />} />
                      <Route path="/compare/:rival" element={<CompareRivalPage />} />
                      <Route path="/changelog" element={<ChangelogPage />} />
                      <Route path="/chains" element={<ChainsPage />} />
                      <Route path="/chains/:chain" element={<ChainsPage />} />
                      <Route path="/use-cases" element={<UseCasesPage />} />
                      <Route path="/use-cases/:useCase" element={<UseCasesPage />} />
                      <Route path="/pricing" element={<PricingPage />} />
                      <Route path="/tools" element={<ToolsPage />} />
                      <Route path="/tools/:tool" element={<ToolsPage />} />
                      <Route path="/faq" element={<FAQPage />} />
                      <Route path="/blog" element={<BlogPage />} />
                      <Route path="/blog/:slug" element={<BlogPage />} />
                      <Route path="/integrations" element={<IntegrationsPage />} />
                      <Route path="/security" element={<SecurityPage />} />
                      <Route path="/testimonials" element={<TestimonialsPage />} />
                      <Route path="/alternative" element={<AlternativesPage />} />
                      <Route path="/alternative/:competitor" element={<AlternativesPage />} />
                      <Route path="/ecosystem" element={<EcosystemPage />} />
                      <Route path="/lang" element={<LanguageLandingPage />} />
                      <Route path="/lang/:lang" element={<LanguageLandingPage />} />
                      <Route path="/track" element={<TokenLandingPage />} />
                      <Route path="/track/:token" element={<TokenLandingPage />} />
                      <Route path="/defi" element={<DeFiProtocolPage />} />
                      <Route path="/defi/:protocol" element={<DeFiProtocolPage />} />
                      <Route path="/learn" element={<LearnPage />} />
                      <Route path="/learn/:course" element={<LearnPage />} />
                      <Route path="/s" element={<SEOLandingPage />} />
                      <Route path="/s/:keyword" element={<SEOLandingPage />} />
                      <Route path="/research" element={<ResearchPage />} />
                      <Route path="/research/:report" element={<ResearchPage />} />
                      <Route path="/roadmap" element={<RoadmapPage />} />
                      <Route path="/for" element={<AudiencePage />} />
                      <Route path="/for/:audience" element={<AudiencePage />} />
                      <Route path="/audience" element={<AudiencePage />} />
                      <Route path="/audience/:audience" element={<AudiencePage />} />
                      <Route path="/why-cryptokody" element={<WhyChoosePage />} />
                      <Route path="/partners" element={<PartnersPage />} />
                      <Route path="/status" element={<StatusPage />} />
                      <Route path="/community" element={<CommunityPage />} />
                      <Route path="/api-docs" element={<ApiDocsPage />} />
                      <Route path="/careers" element={<CareersPage />} />
                      <Route path="/privacy" element={<LegalPage />} />
                      <Route path="/terms" element={<LegalPage />} />
                      <Route path="/whats-new" element={<WhatsNewPage />} />
                      <Route path="/" element={<ProtectedRoute><Web3Wrapper><Index /></Web3Wrapper></ProtectedRoute>} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                    </main>
                  </Suspense>
                </BrowserRouter>
              </TooltipProvider>
            </CurrencyProvider>
          </ThemeProvider>
        </LanguageProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
